// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Period status banner component for displaying period lock status.
 *
 * Shows informational alerts based on the period's state:
 * - Locked initial period: Links to initial true-up page
 * - Locked regular period: Shows lock details, signature, notes
 * - Past unlocked period: Warning to review and lock
 * - Current month (in progress): Info about ongoing period
 * - Future period: Info about provisional data
 */
import { formatPersonName } from '@/account/utilities/format-person-name';
import {
  CloseRunResponse,
  SavedAccountingPeriodActivityStatus,
} from '@/accounting/api/types';
import { formatPeriod } from '@/accounting/utilities/generate-period-list';
import { hasLockedActivity } from '@/accounting/utilities/has-locked-activity';
import { Link } from '@/components/link';
import {
  CalendarTodayOutlined,
  HourglassEmptyOutlined,
  InfoOutlined,
  LockOutlined,
  ScheduleOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  Alert,
  AlertTitle,
  Button,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

interface Props {
  /**
   * The period to display status for.
   */
  period: SavedAccountingPeriodActivityStatus;
  /**
   * The initial true-up period for the organization (optional).
   * Used to identify if this period is the initial true-up period.
   */
  initialTrueUpPeriod?: { year: number; month: number };
  /**
   * The close run data for this period (optional).
   * Used to display signature and notes for locked periods.
   */
  closeRun?: CloseRunResponse | null;
}

/**
 * Checks if a period is in the past (before current calendar month).
 */
function isPeriodInPast(period: SavedAccountingPeriodActivityStatus): boolean {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // JavaScript months are 0-indexed

  if (period.year < currentYear) return true;
  if (period.year === currentYear && period.month < currentMonth) return true;
  return false;
}

/**
 * Checks if a period is the current calendar month.
 */
function isPeriodCurrentMonth(
  period: SavedAccountingPeriodActivityStatus
): boolean {
  const now = new Date();
  return (
    period.year === now.getFullYear() && period.month === now.getMonth() + 1
  );
}

/**
 * Renders a status banner for the period based on its state.
 */
export function PeriodStatusBanner(props: Props) {
  const periodName = formatPeriod(props.period);

  // Derive state from raw data
  const isLocked = hasLockedActivity(props.period);
  const isInitialTrueUpPeriod =
    props.initialTrueUpPeriod &&
    props.period.year === props.initialTrueUpPeriod.year &&
    props.period.month === props.initialTrueUpPeriod.month;
  const isPast = isPeriodInPast(props.period);
  const isCurrentMonth = isPeriodCurrentMonth(props.period);

  // Initial true-up period (locked)
  if (isLocked && isInitialTrueUpPeriod) {
    return (
      <Alert severity="success" icon={<LockOutlined />} sx={{ p: 2 }}>
        <AlertTitle paragraph>
          {periodName} is the initial subledger true-up period
        </AlertTitle>
        <Typography variant="body1" paragraph>
          This period established Hardfin as the asset subledger for your
          organization.
        </Typography>
        <Typography variant="body1" paragraph>
          Full details including the onboarding journal entry and GL balance
          reconciliation are available in the initial true-up.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          component={Link}
          to="/accounting/initial-true-up"
        >
          View initial true-up details
        </Button>
      </Alert>
    );
  }

  // Regular locked period with full details
  const periodClose = props.period.periodClose;
  const initialTrueUps = props.period.initialTrueUps;
  if (isLocked && periodClose) {
    const lockDate = periodClose.lockedAt.toLocaleString({
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    const fullLockDateTime = periodClose.lockedAt.toLocaleString({
      dateStyle: 'full',
      timeStyle: 'long',
    });

    const lockedByName = formatPersonName(periodClose.lockedBy);

    return (
      <Alert severity="success" icon={<LockOutlined />} sx={{ p: 2 }}>
        <AlertTitle paragraph>{periodName} is locked</AlertTitle>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body1" paragraph>
              Balances and journal entries are final and read-only.
            </Typography>
            <Tooltip
              placement="right"
              arrow
              title="Any updates from new operational data in this period will be handled automatically via prior-period adjustments during the next open period"
            >
              <InfoOutlined fontSize="small" />
            </Tooltip>
          </Stack>
          <Typography variant="body1" paragraph>
            This period was locked by <strong>{lockedByName}</strong> on{' '}
            <Tooltip arrow title={fullLockDateTime}>
              <strong
                style={{
                  cursor: 'help',
                  textDecoration: 'underline dotted',
                }}
              >
                {lockDate}
              </strong>
            </Tooltip>
            .
          </Typography>

          {initialTrueUps.map((initialTrueUp) => (
            <Typography
              key={initialTrueUp.closeRunId}
              variant="body1"
              paragraph
            >
              The {initialTrueUp.currency} initial true-up was locked by{' '}
              <strong>{formatPersonName(initialTrueUp.lockedBy)}</strong> on{' '}
              <strong>
                {initialTrueUp.lockedAt.toLocaleString({
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </strong>
              .
            </Typography>
          ))}

          {props.closeRun?.lockedSignature && (
            <Stack
              direction="row"
              spacing={0.5}
              justifyContent="flex-start"
              alignItems="center"
            >
              <Typography variant="subtitle1">Signature:</Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                fontFamily="cursive"
              >
                {props.closeRun.lockedSignature}
              </Typography>
            </Stack>
          )}

          {props.closeRun?.lockedNotes && (
            <Stack
              direction="row"
              spacing={0.5}
              justifyContent="flex-start"
              alignItems="center"
            >
              <Typography variant="subtitle1">Notes:</Typography>
              <Typography variant="body1" color="text.secondary">
                {props.closeRun.lockedNotes}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Alert>
    );
  }

  // Show simplified locked banner if we don't have detailed status yet
  if (isLocked) {
    return (
      <Alert severity="success" icon={<LockOutlined />} sx={{ p: 2 }}>
        <AlertTitle paragraph>{periodName} is locked</AlertTitle>
        <Typography variant="body1">
          Balances and journal entries are final and read-only.
        </Typography>
      </Alert>
    );
  }

  // Past period that is not yet locked
  if (!isLocked && isPast) {
    return (
      <Alert severity="warning" icon={<ScheduleOutlined />} sx={{ p: 2 }}>
        <AlertTitle paragraph>{periodName} is not yet locked</AlertTitle>
        <Typography variant="body1" sx={{ mb: 3 }}>
          This period has ended but has not been reviewed and locked.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          component={Link}
          to="/accounting/close-workflow"
        >
          Review and lock this period
        </Button>
      </Alert>
    );
  }

  // Current month (in progress, not yet complete)
  if (!isLocked && isCurrentMonth) {
    return (
      <Alert severity="info" icon={<HourglassEmptyOutlined />} sx={{ p: 2 }}>
        <AlertTitle paragraph>Period not yet complete</AlertTitle>
        <Typography variant="body1">
          <strong>{periodName}</strong> is the current accounting period and has
          not yet ended. Activity and balances are <strong>in progress</strong>{' '}
          and may change as new data is added.
        </Typography>
      </Alert>
    );
  }

  // Future period
  if (!isLocked) {
    return (
      <Alert severity="info" icon={<CalendarTodayOutlined />} sx={{ p: 2 }}>
        <AlertTitle paragraph>Future period</AlertTitle>
        <Typography variant="body1">
          <strong>{periodName}</strong> is a future accounting period. Activity
          and balances are <strong>provisional</strong> and will be finalized
          once this period is reached and subsequently locked.
        </Typography>
      </Alert>
    );
  }

  return null;
}
